/**
 * Etiquetado frontal de advertencia — NOM-051-SCFI/SSA1-2010 (modificación 2020).
 *
 * Calcula los sellos octagonales "EXCESO DE…" y las leyendas precautorias a
 * partir de la información nutrimental capturada en el admin. Los umbrales
 * provienen del Apéndice A (Tabla de nutrimentos críticos) de la NOM-051
 * vigente para la fase final del etiquetado.
 *
 * Referencia: https://www.dof.gob.mx/normasOficiales/8150/seeco11_C/seeco11_C.html
 *
 * Todos los valores se declaran por 100 g (sólidos) o 100 mL (líquidos), tal
 * como exige la norma para evaluar los nutrimentos críticos.
 */

export type SealKey = "calories" | "sugars" | "satFat" | "transFat" | "sodium";
export type LegendKey = "sweeteners" | "caffeine";

/** Estado manual de cada sello/leyenda: deriva del cálculo o se fuerza. */
export type SealOverride = "auto" | "on" | "off";

export interface Micronutrient {
  /** Nombre, p.ej. "Vitamina C", "Hierro". */
  name: string;
  /** Cantidad por porción. */
  amount: number | null;
  /** Unidad, p.ej. "mg", "µg", "UI". */
  unit: string;
  /** % del Valor Nutrimental de Referencia (opcional). */
  vnr?: number | null;
}

export interface NutritionData {
  /** Etiqueta de porción, p.ej. "1 taza (30 g)". */
  portionLabel?: string | null;
  /** Porciones por envase. */
  portionsPerPackage?: number | null;
  /** Base de declaración de los nutrimentos críticos. */
  basis?: "100g" | "100ml";
  /** true = líquido (umbrales por 100 mL); false = sólido (por 100 g). */
  isLiquid?: boolean;

  // Tabla nutrimental — valores por base (100 g / 100 mL)
  energyKcal?: number | null;
  proteinG?: number | null;
  totalFatG?: number | null;
  saturatedFatG?: number | null;
  transFatG?: number | null;
  carbsG?: number | null;
  sugarsG?: number | null;
  addedSugarsG?: number | null;
  fiberG?: number | null;
  sodiumMg?: number | null;

  // Composición que dispara/condiciona sellos y leyendas
  hasAddedSugars?: boolean;
  hasAddedFats?: boolean;
  hasAddedSodium?: boolean;
  hasSweeteners?: boolean;
  hasCaffeine?: boolean;

  // Tabla de vitaminas y minerales
  micronutrients?: Micronutrient[];

  /** Sobrescritura manual por sello/leyenda. Ausente = "auto". */
  overrides?: Partial<Record<SealKey | LegendKey, SealOverride>>;
}

export const SEAL_LABELS: Record<SealKey, string> = {
  calories: "EXCESO CALORÍAS",
  sugars: "EXCESO AZÚCARES",
  satFat: "EXCESO GRASAS SATURADAS",
  transFat: "EXCESO GRASAS TRANS",
  sodium: "EXCESO SODIO",
};

export const LEGEND_LABELS: Record<LegendKey, string> = {
  sweeteners: "CONTIENE EDULCORANTES, NO RECOMENDABLE EN NIÑOS",
  caffeine: "CONTIENE CAFEÍNA, EVITAR EN NIÑOS",
};

export const SEAL_ORDER: SealKey[] = ["calories", "sugars", "satFat", "transFat", "sodium"];
export const LEGEND_ORDER: LegendKey[] = ["sweeteners", "caffeine"];

// Factores de Atwater (kcal por gramo de nutrimento).
const KCAL_PER_G_SUGAR = 4;
const KCAL_PER_G_FAT = 9;

/**
 * Evalúa los sellos a partir de los valores nutrimentales (sin overrides).
 * Devuelve `true` cuando el producto rebasa el umbral de la norma.
 *
 * Fuente: NOM-051-SCFI/SSA1-2010 (DOF 27-mar-2020), numeral 4.5.3 y Tabla 6,
 * aplicable desde la tercera fase (1-oct-2025).
 *
 * Las casillas de "añadidos" del admin no son confiables (la mayoría quedaron
 * en false por defecto), así que no bloquean sellos. Sólo el de azúcares exige
 * evidencia de azúcar añadido: la casilla marcada o azúcares añadidos > 0 g.
 */
export function computeAutoSeals(d: NutritionData): Record<SealKey, boolean> {
  const liquid = !!d.isLiquid || d.basis === "100ml";
  const energy = num(d.energyKcal);
  const hasEnergy = energy !== null && energy > 0;
  const declaredAdded = num(d.addedSugarsG);
  const addsSugars = !!d.hasAddedSugars || (declaredAdded !== null && declaredAdded > 0);

  // Calorías: ≥ 275 kcal/100 g (sólidos) o ≥ 70 kcal/100 mL (líquidos).
  const calories = energy !== null && energy >= (liquid ? 70 : 275);

  // Azúcares libres. Sólidos: ≥ 10% de la energía total. Líquidos: ≥ 8 kcal
  // de azúcares libres por 100 mL. Si no hay dato de azúcares añadidos, los
  // totales son una cota superior y sólo se usan cuando hay añadido de azúcar.
  const freeSugars = declaredAdded ?? (addsSugars ? num(d.sugarsG) : null);
  const freeSugarKcal = freeSugars !== null ? freeSugars * KCAL_PER_G_SUGAR : null;
  const sugars =
    addsSugars &&
    freeSugarKcal !== null &&
    (liquid ? freeSugarKcal >= 8 : hasEnergy && freeSugarKcal / energy! >= 0.1);

  // Grasas saturadas: ≥ 10% de la energía total.
  const sat = num(d.saturatedFatG);
  const satFat = hasEnergy && sat !== null && (sat * KCAL_PER_G_FAT) / energy! >= 0.1;

  // Grasas trans: ≥ 1% de la energía total.
  const trans = num(d.transFatG);
  const transFat = hasEnergy && trans !== null && (trans * KCAL_PER_G_FAT) / energy! >= 0.01;

  // Sodio. Bebidas sin calorías: ≥ 45 mg. Resto: ≥ 1 mg por kcal o ≥ 300 mg.
  const sodium = num(d.sodiumMg);
  const zeroCalBeverage = liquid && energy === 0;
  const sodiumSeal =
    sodium !== null &&
    (zeroCalBeverage
      ? sodium >= 45
      : (hasEnergy && sodium / energy! >= 1) || sodium >= 300);

  return { calories, sugars, satFat, transFat, sodium: sodiumSeal };
}

/** Leyendas precautorias automáticas (dependen sólo de la composición). */
export function computeAutoLegends(d: NutritionData): Record<LegendKey, boolean> {
  return {
    sweeteners: !!d.hasSweeteners,
    caffeine: !!d.hasCaffeine,
  };
}

/** Aplica el override manual sobre el valor automático. */
function resolve(auto: boolean, override: SealOverride | undefined): boolean {
  if (override === "on") return true;
  if (override === "off") return false;
  return auto;
}

export interface ResolvedLabeling {
  seals: SealKey[];
  legends: LegendKey[];
}

/**
 * Sellos y leyendas finales a mostrar, combinando cálculo automático con los
 * overrides manuales capturados en el admin.
 */
export function resolveLabeling(d: NutritionData | null | undefined): ResolvedLabeling {
  if (!d) return { seals: [], legends: [] };
  const autoSeals = computeAutoSeals(d);
  const autoLegends = computeAutoLegends(d);
  const ov = d.overrides ?? {};

  const seals = SEAL_ORDER.filter((k) => resolve(autoSeals[k], ov[k]));
  const legends = LEGEND_ORDER.filter((k) => resolve(autoLegends[k], ov[k]));
  return { seals, legends };
}

/** Indica si hay datos suficientes para calcular sellos automáticamente. */
export function hasNutritionData(d: NutritionData | null | undefined): boolean {
  if (!d) return false;
  return (
    num(d.energyKcal) !== null ||
    num(d.sugarsG) !== null ||
    num(d.saturatedFatG) !== null ||
    num(d.sodiumMg) !== null ||
    (d.micronutrients?.length ?? 0) > 0
  );
}

function num(v: number | null | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
